<script setup lang="ts">
import { usePejer } from '@/Pejer'
import { InvoiceCreationResponse } from '@/Pejer/types/invoice'
import { toTypedSchema } from '@vee-validate/zod'
import { Building2, FileText, UserRound } from 'lucide-vue-next'
import AddressField from 'pejer/src/components/Form/AddressField.vue'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'pejer/src/components/ui/form'
import { Input } from 'pejer/src/components/ui/input'
import Separator from 'pejer/src/components/ui/separator/Separator.vue'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'pejer/src/components/ui/tabs'
import { Customer } from 'pejer/src/tables/Customer'
import { CustomerTypeEnum } from 'pejer/src/tables/Global'
import usePejerMutation from 'pejer/src/usePejerMutation'
import { z } from 'zod'
import PayButton from '../PayButton.vue'
import { invoiceOptionsSchema } from './InvoicePaymentOptionSchema'
const props = defineProps<{
  paymentReferenceID: string
  currency: string
  grossAmount: number
  customer: Customer.Full
}>()

const emits = defineEmits<{
  (e: 'onInvoicePaid', value: InvoiceCreationResponse): void
}>()

const formSchema = toTypedSchema(invoiceOptionsSchema)

// Mobilen Bloc sendte med kunden. Norske nummer uten landskode, andre med (+46...).
const initialMobile = props.customer?.mobile
  ? props.customer.mobilePrefix && props.customer.mobilePrefix.replace(/s/g, '') !== '+47'
    ? `${props.customer.mobilePrefix}${props.customer.mobile}`.replace(/s/g, '')
    : props.customer.mobile.replace(/s/g, '')
  : ''

const pejer = usePejer()
const { mutate, dataState } = usePejerMutation(
  pejer.urls.checkout.createInvoice,
  {
    queryKeys: [['payments']],
    onSuccess: (data: InvoiceCreationResponse) => {
      emits('onInvoicePaid', data)
    },
  },
  {
    ignoreAuthorization: true,
  }
)

const onSubmit = (values: z.infer<typeof invoiceOptionsSchema>) => {
  console.log('values', values)
  const formattedValues = {
    ...values,
  }
  if (values.customerType === CustomerTypeEnum.Individual) {
    formattedValues.customerOrganizationNumber = null
  } else if (values.customerType === CustomerTypeEnum.Business) {
    formattedValues.customerSSN = null
  }
  mutate({
    pathArgs: {
      paymentReferenceID: props.paymentReferenceID,
    },
    body: formattedValues,
  })
}
</script>
<template>
  <Form
    :validation-schema="formSchema"
    :initial-values="{
      customerType: customer?.customerType || CustomerTypeEnum.Individual,
      customerEmail: customer?.email,
      customerMobile: initialMobile,
      customerName: customer?.name,
      customerAddress: '',
      customerPostalCode: '',
      customerCity: '',
      customerSSN: '',
      customerOrganizationNumber: '',
    }"
    class="h-full"
    keep-values
    @submit="onSubmit"
    v-slot="{ setFieldValue, values }"
  >
    <div class="flex flex-col w-full h-full justify-between gap-2">
      <Tabs
        :default-value="values.customerType"
        class="w-full my-2"
        drop-query-params
        @update:model-value="setFieldValue('customerType', $event)"
      >
        <TabsList class="grid w-full grid-cols-2 mb-2">
          <TabsTrigger :value="CustomerTypeEnum.Individual">
            <span class="text-sm text-slate-500 flex gap-2 items-center">
              <UserRound :size="16" /> Privatperson
            </span>
          </TabsTrigger>
          <TabsTrigger :value="CustomerTypeEnum.Business">
            <span class="text-sm text-slate-500 flex gap-2 items-center">
              <Building2 :size="16" /> Bedrift
            </span>
          </TabsTrigger>
        </TabsList>
        <TabsContent
          :value="CustomerTypeEnum.Individual"
          class="border border-slate-200 rounded-md p-6 shadow-sm shadow-slate-200"
        >
          <p class="text-sm text-slate-500 flex gap-2 items-center">
            <FileText :size="16" /> Fakturadetaljer
          </p>
          <Separator class="my-4" />
          <div class="flex flex-col gap-4">
            <!-- name, address, SSN and email -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <FormField
                v-slot="{ componentField }"
                name="customerName"
                :validate-on-mount="!!customer?.name"
              >
                <FormItem>
                  <FormLabel class="text-secondaryBlack">Navn</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="Ola Nordmann" v-bind="componentField" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>
              <FormField v-slot="{ componentField }" name="customerSSN">
                <FormItem>
                  <FormLabel class="text-secondaryBlack">Fødselsnummer</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="12345678910" v-bind="componentField" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>
            <FormField
              v-slot="{ componentField }"
              name="customerEmail"
              :validate-on-mount="!!customer?.email"
            >
              <FormItem>
                <FormLabel class="text-secondaryBlack">E-post</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Ola.Nordmann@example.no"
                    v-bind="componentField"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
            <FormField
              v-slot="{ componentField }"
              name="customerMobile"
              :validate-on-mount="!!customer?.mobile"
            >
              <FormItem>
                <FormLabel class="text-secondaryBlack">Mobilnummer</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="91234567" v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <AddressField
              addressName="customerAddress"
              postalCodeName="customerPostalCode"
              cityName="customerCity"
              ignore-validation-on-mount
            />
          </div>
        </TabsContent>
        <TabsContent
          :value="CustomerTypeEnum.Business"
          class="flex-col gap-4 border border-slate-200 rounded-md p-6 shadow-sm shadow-slate-200"
        >
          <p class="text-sm text-slate-500 flex gap-2 items-center">
            <FileText :size="16" /> Fakturadetaljer
          </p>
          <Separator class="my-4" />
          <div class="flex flex-col gap-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <FormField v-slot="{ componentField }" name="customerOrganizationNumber">
                <FormItem>
                  <FormLabel class="text-slate-700 text-sm">Organisasjonsnummer</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="123 456 789" v-bind="componentField" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>
              <FormField v-slot="{ componentField }" name="customerName" :model-value="''">
                <!-- :validate-on-mount="!!customer?.name" -->
                <FormItem>
                  <FormLabel class="text-secondaryBlack">Organisasjonsnavn</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="Acme Inc" v-bind="componentField" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </FormField>
            </div>
            <FormField v-slot="{ componentField }" name="customerEmail">
              <!-- :validate-on-mount="!!customer?.email" -->
              <FormItem>
                <FormLabel class="text-secondaryBlack">E-post</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="invoice@acme.com" v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
            <FormField
              v-slot="{ componentField }"
              name="customerMobile"
              :validate-on-mount="!!customer?.mobile"
            >
              <FormItem>
                <FormLabel class="text-secondaryBlack">Mobilnummer</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="91234567" v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <AddressField
              addressName="customerAddress"
              postalCodeName="customerPostalCode"
              cityName="customerCity"
              ignore-validation-on-mount
            />
          </div>
        </TabsContent>
      </Tabs>

      <div>
        <p class="text-sm text-slate-500 my-2">
          Du vil motta en faktura på e-post etter at bestillingen er bekreftet.
        </p>
        <PayButton :data-state="dataState" :currency="currency" :gross-amount="grossAmount">
          <template #text="{ amount }">
            <!-- <FileText class="w-6 h-6" /> -->
            <span>Bekreft betaling på {{ amount }}</span>
          </template>
        </PayButton>
      </div>
    </div>
  </Form>
</template>
<style lang="css" scoped>
:deep(input) {
  height: 40px !important;
}
:deep(label) {
  @apply text-gray-500;
  /* @apply text-sm; */
  /* @apply font-normal; */
}
input::placeholder {
  @apply text-gray-200;
}
</style>
